// ===========================================
// BUSTED! BLACKJACK - USE VISIBILITY HANDLER HOOK
// ===========================================
// Handles tab visibility changes for resync:
// - Hidden for 5+ seconds: clear state (enter away mode)
// - Visible again: trigger full resync
// ===========================================

import { useEffect, useRef, useCallback } from 'react';
import { getClientTableMaster } from '../utils/ClientTableMaster';
import { useGameStateStore } from '../stores/gameStateStore';

/** Threshold before clearing state when tab is hidden (milliseconds) */
const HIDDEN_THRESHOLD_MS = 5000;

export interface UseVisibilityHandlerOptions {
  /** Called when tab becomes visible after being hidden for threshold */
  onNeedsResync: () => void;
  /** Whether to enable visibility handling (disable for pages without game) */
  enabled?: boolean;
}

/**
 * Hook to handle tab visibility changes for game state resync.
 *
 * Behavior:
 * - When tab hidden for 5+ seconds: enter away mode, clear dynamic state
 * - When tab becomes visible after clearing: trigger resync callback
 *
 * This ensures:
 * - No stale state persists after returning to tab
 * - Fresh state is always fetched from server
 * - Browser resource constraints during hidden state don't corrupt UI
 */
export function useVisibilityHandler(options: UseVisibilityHandlerOptions): void {
  const { onNeedsResync, enabled = true } = options;

  // Track state
  const hiddenTimestampRef = useRef<number | null>(null);
  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const needsResyncRef = useRef(false);

  // Get state clearing function
  const clearGameStateForResync = useGameStateStore((state) => state.clearGameStateForResync);

  const handleVisibilityChange = useCallback(() => {
    if (!enabled) return;

    const isHidden = document.hidden;

    if (isHidden) {
      // Tab became hidden - start timer
      hiddenTimestampRef.current = Date.now();
      console.log('[useVisibilityHandler] Tab hidden - starting clear timer');

      // Set timeout to clear state after threshold
      clearTimeoutRef.current = setTimeout(() => {
        const tableId = useGameStateStore.getState().tableId;
        if (!tableId) {
          console.log('[useVisibilityHandler] No table ID, skipping clear');
          return;
        }

        console.log('[useVisibilityHandler] Hidden threshold reached - clearing state');

        // Enter away mode on CTM
        const ctm = getClientTableMaster();
        ctm.enterAwayMode();

        // Clear dynamic game state
        clearGameStateForResync();

        // Mark that we need resync when visible
        needsResyncRef.current = true;
      }, HIDDEN_THRESHOLD_MS);

    } else {
      // Tab became visible
      const wasHidden = hiddenTimestampRef.current !== null;
      const hiddenDuration = wasHidden ? Date.now() - hiddenTimestampRef.current! : 0;

      // Clear pending timeout if tab becomes visible before threshold
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
        clearTimeoutRef.current = null;
      }

      // Reset hidden timestamp
      hiddenTimestampRef.current = null;

      console.log(`[useVisibilityHandler] Tab visible - was hidden for ${hiddenDuration}ms`);

      // If we cleared state while hidden, trigger resync
      if (needsResyncRef.current) {
        console.log('[useVisibilityHandler] Triggering resync');
        needsResyncRef.current = false;
        onNeedsResync();
      } else if (wasHidden && hiddenDuration >= HIDDEN_THRESHOLD_MS) {
        // Edge case: if visibility change happened right at threshold boundary
        console.log('[useVisibilityHandler] Hidden at threshold boundary - triggering resync');
        onNeedsResync();
      }
    }
  }, [enabled, onNeedsResync, clearGameStateForResync]);

  useEffect(() => {
    if (!enabled) return;

    // Listen to visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
      }
    };
  }, [enabled, handleVisibilityChange]);

  // Also handle page unload to clean up state
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = () => {
      // Exit away mode if in it (cleanup)
      const ctm = getClientTableMaster();
      if (ctm.isInAwayMode()) {
        const state = useGameStateStore.getState();
        ctm.exitAwayMode(state.round?.roundNumber ?? 0, state.phase);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled]);
}

export default useVisibilityHandler;
